<template>
  <div class="experiences-filter">
    <modal
      ref="filterSidebar"
      variant="drawer"
      custom-footer
      @input="handleOpenModal"
      @close="handleCloseModal"
    >
      <template #default="{ activate, isActive }">
        <slot v-bind="{ activate, isActive, resetAllFilters, totalFiltersSelected }" />
      </template>

      <template #header>
        <div class="experiences-filter--modal-title">
          <span>{{ dictionary.filters }}</span>
        </div>
      </template>

      <template #body>
        <div
          ref="accordionsWrapper"
          class="form"
        >
          <div class="accordion component">
            <ul
              class="accordion-wrapper container"
              aria-label="Accordion Control Group Buttons"
            >
              <accordion-tab
                v-if="shouldShowPropertyFilter && availablePropertiesByRegion.length > 0"
                ref="accordionProperty"
                start-expanded
              >
                <li
                  slot-scope="{ toggleTab, expanded }"
                  class="accordion-item"
                  :class="expanded ? 'expanded' : ''"
                >
                  <button
                    id="accordion-control-id"
                    class="accordion-item-label experiences-filter--filter-group"
                    :class="expanded ? 'expanded' : ''"
                    aria-controls="content-id"
                    :aria-expanded="expanded ? 'true' : 'false'"
                    @click.prevent="toggleTab"
                    @enter="toggleTab"
                    @keydown.space.prevent="toggleTab"
                  >
                    <div>
                      <span class="experiences-filter--filter-group-title">{{ dictionary.property }}</span>
                      <button
                        v-if="selectedProperties.length > 0"
                        class="experiences-filter--filter-group-reset"
                        @click.stop="resetPropertyFilter"
                      >
                        {{ dictionary.reset }}
                      </button>
                    </div>
                    <span
                      v-if="selectedProperties.length > 0"
                      class="experiences-filter--filter-group-status"
                    >{{ selectedProperties.length }}</span>
                  </button>

                  <div
                    id="content-id"
                    class="accordion-item-content"
                    :aria-hidden="expanded ? 'false' : 'true'"
                  >
                    <div class="accordion-item-content-wrapper">
                      <div
                        v-for="(region, index) in availablePropertiesByRegion"
                        :key="index"
                        class="region-experience-group"
                      >
                        <div
                          v-if="region.properties.length > 0"
                          class="region-experience-name"
                        >
                          <div class="input checkbox">
                            <label
                              :for="region.name"
                              :class="{ disabled: region.isRegionCheckboxDisabled }"
                            >
                              <input
                                :id="region.name"
                                type="checkbox"
                                data-field-type="CheckBoxSet"
                                :tabindex="expanded ? '0' : '-1'"
                                :value="region.name"
                                :disabled="region.isRegionCheckboxDisabled"
                                :checked="areAllRegionPropertiesSelected(region)"
                                @click="selectAllPropertiesInRegion(region.name)"
                              >
                              {{ region.name }}
                            </label>
                          </div>
                        </div>
                        <ul
                          v-if="region.properties.length > 0"
                          class="experiences-filter--filter-group-properties"
                        >
                          <li
                            v-for="item in region.properties"
                            class="input checkbox"
                          >
                            <label
                              :for="item.name"
                              :class="{ disabled: propertyIsDisabled(item.name) }"
                            >
                              <input
                                :id="item.name"
                                v-model="selectedProperties"
                                type="checkbox"
                                data-field-type="CheckBoxSet"
                                :tabindex="expanded ? '0' : '-1'"
                                :value="item.name"
                                :disabled="propertyIsDisabled(item.name)"
                              >
                              {{ item.name }}
                            </label>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </li>
              </accordion-tab>
              <accordion-tab
                ref="accordionDate"
                start-expanded
              >
                <li
                  slot-scope="{ toggleTab, expanded }"
                  class="accordion-item date-filter"
                  :class="expanded ? 'expanded' : ''"
                >
                  <button
                    id="accordion-control-id"
                    class="accordion-item-label experiences-filter--filter-group "
                    :class="expanded ? 'expanded' : ''"
                    aria-controls="content-id"
                    :aria-expanded="expanded ? 'true' : 'false'"
                    @click.prevent="toggleTab"
                    @enter="toggleTab"
                    @keydown.space.prevent="toggleTab"
                  >
                    <div>
                      <span class="experiences-filter--filter-group-title">{{ dictionary.pickADate }}</span>
                      <button
                        v-if="isDateSelected"
                        class="experiences-filter--filter-group-reset"
                        @click.stop="resetDateFilter"
                      >
                        {{ dictionary.reset }}
                      </button>
                    </div>
                    <span
                      v-if="isDateSelected"
                      class="experiences-filter--filter-group-status date-filter"
                    >{{ formatDateRange(selectedDates.startDate, selectedDates.endDate) }}</span>
                  </button>

                  <div
                    id="content-id"
                    class="accordion-item-content"
                    :aria-hidden="expanded ? 'false' : 'true'"
                  >
                    <div
                      v-show="expanded"
                      class="accordion-item-content-wrapper"
                    >
                      <div class="experiences-filter--filter-group-date form">
                        <div class="input">
                          <date-field-input
                            ref="startDate"
                            :lang="options.calendarLang"
                            :emit-event="true"
                            :min-date="todaysDate"
                            :max-date="oneYearLaterDate"
                            :tabindex="expanded ? '0' : '-1'"
                            :aria-hidden="expanded ? 'false' : 'true'"
                            @dateChange="handleStartDateChange"
                          >
                            <template slot-scope="{ toggleCalendar, onChange }">
                              <input
                                id="exp-start-date"
                                type="date"
                                :value="selectedDates.startDate"
                                name="exp-start-date"
                                :max="oneYearLaterDate"
                                :class="{ 'selected-date': selectedDates.startDate }"
                                data-field-type="DateField"
                                :tabindex="expanded ? '0' : '-1'"
                                :aria-hidden="expanded ? 'false' : 'true'"
                                @change="onChange"
                              >
                              <label
                                for="exp-start-date"
                                class="floating"
                              >
                                {{ dictionary.from }}:
                              </label>

                              <button
                                class="icon-calendar"
                                :tabindex="expanded ? '0' : '-1'"
                                :aria-hidden="expanded ? 'false' : 'true'"
                                @click.prevent="toggleCalendar"
                              />
                            </template>
                          </date-field-input>
                        </div>
                        <div class="input">
                          <date-field-input
                            ref="endDate"
                            :lang="options.calendarLang"
                            :emit-event="true"
                            :min-date="selectedDates.startDate"
                            :max-date="oneYearLaterDate"
                            :tabindex="expanded ? '0' : '-1'"
                            :aria-hidden="expanded ? 'false' : 'true'"
                            @dateChange="handleEndDateChange"
                          >
                            <template slot-scope="{ toggleCalendar, onChange }">
                              <input
                                id="exp-end-date"
                                type="date"
                                :value="selectedDates.endDate"
                                name="exp-end-date"
                                :max="oneYearLaterDate"
                                :class="{ 'selected-date': selectedDates.endDate }"
                                data-field-type="DateField"
                                :tabindex="expanded ? '0' : '-1'"
                                :aria-hidden="expanded ? 'false' : 'true'"
                                @change="onChange"
                              >
                              <label
                                for="exp-end-date"
                                class="floating"
                              >
                                {{ dictionary.to }}:
                              </label>

                              <button
                                class="icon-calendar"
                                :tabindex="expanded ? '0' : '-1'"
                                :aria-hidden="expanded ? 'false' : 'true'"
                                @click.prevent="toggleCalendar"
                              />
                            </template>
                          </date-field-input>
                        </div>
                      </div>
                    </div>
                    <div
                      v-show="expanded"
                      class="accordion-item-content-bottom"
                    >
                      <button
                        v-if="isDateSelected"
                        class="experiences-filter--filter-group-reset"
                        @click.stop="resetDateFilter"
                      >
                        {{ dictionary.reset }}
                      </button>
                    </div>
                  </div>
                </li>
              </accordion-tab>
              <accordion-tab
                v-if="availableSeasonsByCategory.length > 0"
                ref="accordionSeason"
                start-expanded
              >
                <li
                  slot-scope="{ toggleTab, expanded }"
                  class="accordion-item"
                  :class="expanded ? 'expanded' : ''"
                >
                  <button
                    id="accordion-control-id"
                    class="accordion-item-label experiences-filter--filter-group"
                    :class="expanded ? 'expanded' : ''"
                    aria-controls="content-id"
                    :aria-expanded="expanded ? 'true' : 'false'"
                    @click.prevent="toggleTab"
                    @enter="toggleTab"
                    @keydown.space.prevent="toggleTab"
                  >
                    <div>
                      <span class="experiences-filter--filter-group-title">{{ dictionary.seasons }}</span>
                      <button
                        v-if="selectedSeasons.length > 0"
                        class="experiences-filter--filter-group-reset"
                        @click.stop="selectedSeasons = []"
                      >
                        {{ dictionary.reset }}
                      </button>
                    </div>
                    <span
                      v-if="selectedSeasons.length > 0"
                      class="experiences-filter--filter-group-status"
                    >{{ selectedSeasons.length }}</span>
                  </button>

                  <div
                    id="content-id"
                    class="accordion-item-content seasons-filter"
                    :aria-hidden="expanded ? 'false' : 'true'"
                  >
                    <div class="accordion-item-content-wrapper">
                      <ul class="experiences-filter--filter-group-seasons">
                        <li
                          v-for="item in availableSeasonsByCategory"
                          class="input checkbox"
                        >
                          <label
                            :for="item.name"
                            :class="{ disabled: seasonIsDisabled(item.name) }"
                          >
                            <input
                              :id="item.name"
                              v-model="selectedSeasons"
                              type="checkbox"
                              data-field-type="CheckBoxSet"
                              :tabindex="expanded ? '0' : '-1'"
                              :value="item.name"
                              :disabled="seasonIsDisabled(item.name)"
                            >
                            {{ item.name }}
                          </label>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </accordion-tab>
            </ul>
          </div>
          <div
            v-if="onlineBookingSettingExist"
            class="experiences-filter--online-booking"
          >
            <label>
              <input
                id="online_booking"
                v-model="isOnlineBookingSelected"
                data-field-type="CheckBoxSet"
                name="online_booking"
                type="checkbox"
                :aria-label="dictionary.onlineBooking"
                :disabled="onlineBookingIsDisabled()"
              >
              {{ dictionary.onlineBooking }}
            </label>
          </div>
          <div
            v-if="showFanExclusiveFilter && exclusiveExperiencesExist"
            class="experiences-filter--fan-exclusive"
          >
            <label>
              <input
                id="fan_exlusive"
                v-model="isFanExclusiveSelected"
                data-field-type="CheckBoxSet"
                name="fan_exlusive"
                type="checkbox"
                :aria-label="dictionary.fansOfMOExclusive"
                :disabled="fanExlusiveIsDisabled()"
              >
              {{ dictionary.fansOfMOExclusive }}
            </label>
          </div>
          <div
            v-if="eliteExclusiveExperiencesExist"
            class="experiences-filter--elite-fan-exclusive"
          >
            <label>
              <input
                id="elite_fan_exlusive"
                v-model="isEliteFanExclusiveSelected"
                data-field-type="CheckBoxSet"
                name="elite_fan_exlusive"
                type="checkbox"
                :aria-label="dictionary.fansOfMOEliteExclusive"
                :disabled="eliteFanExlusiveIsDisabled()"
              >
              {{ dictionary.fansOfMOEliteExclusive }}
            </label>
          </div>
        </div>
      </template>

      <template #footer="{ deactivate }">
        <div class="experiences-filter--footer">
          <div class="experiences-filter--footer-results">
            <div class="experiences-filter--footer-results-text">
              {{ filteredExperiences.length }} {{ dictionary.matchingResults }}
            </div>
            <button
              v-if="hasFilterSelected"
              class="experiences-filter--footer-results-reset"
              @click="resetAllFilters"
            >
              {{ dictionary.resetAll }}
            </button>
          </div>
          <div class="experiences-filter--footer-cta">
            <button @click="deactivate">
              {{ dictionary.apply }}
            </button>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import sift from 'sift'
  import DateFieldInput from '@components/01.base/01.forms/inputs/date-field-input.vue'
  import moment from 'moment'

  export default {
    name: 'ExperiencesFilter',
    components: { DateFieldInput },
    props: {
      experienceTypeFilter: {
        type: Object,
        required: true,
        default: () => ({})
      },
      experiences: {
        type: Array,
        required: true,
        default: () => ([])
      },
      options: {
        type: Object,
        required: true,
        default: () => ({})
      },
      dictionary: {
        type: Object,
        required: true,
        default: () => ({})
      },
    },
    data() {
      return {
        isOpen: false,
        availableProperties: [],
        availableSeasons: [],
        selectedDates: {
          startDate: null,
          endDate: null
        },
        selectedProperties: [],
        selectedSeasons: [],
        filteredExperiences: [],
        isFanExclusiveSelected: false,
        isEliteFanExclusiveSelected: false,
        isOnlineBookingSelected: false,
        isDateSelected: false,
      }
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width', wHeight: 'height' }),

      filteredExperiencesByCategory() {
        return this.experiences.filter(sift(this.experienceTypeFilter))
      },

      availablePropertiesByCategory() {
        return this.getAvailableProperties(this.filteredExperiencesByCategory)
      },

      availablePropertiesByRegion() {
        const regions = {}
        if (this.availablePropertiesByCategory && this.availablePropertiesByCategory.length > 0) {
          this.availablePropertiesByCategory.forEach((hotel) => {
            const regionName = hotel.propertyRegion
            if (!regions[regionName]) {
              regions[regionName] = {
                name: regionName,
                properties: [],
                isRegionCheckboxDisabled: false,
              }
            }
            regions[regionName].properties.push(hotel)
          })
        }

        const regionsOrder = this.options.regionSectionsOrder && this.options.regionSectionsOrder.length > 0
          ? this.options.regionSectionsOrder
          : Object.keys(regions).map((regionName) => ({ region: regionName }))

        return regionsOrder.map((regionItem) => {
          const region = Object.values(regions).find((item) => item.name === regionItem.region)

          if (region) {
            region.properties.sort((a, b) => a.name.localeCompare(b.name))
            region.isRegionCheckboxDisabled = this.regionIsDisabled(region)
          }

          return region || { name: regionItem.region, properties: [] }
        })
      },

      availableSeasonsByCategory() {
        const availableSeasons = this.getAvailableSeasons(this.filteredExperiencesByCategory)
        const { seasonsOrder } = this.options

        if (seasonsOrder && seasonsOrder.length > 0) {
          availableSeasons.sort((a, b) => {
            const indexA = seasonsOrder.findIndex((season) => season.id === a.id)
            const indexB = seasonsOrder.findIndex((season) => season.id === b.id)
            return indexA - indexB
          })
        }

        return availableSeasons
      },

      totalFiltersSelected() {
        let total = 0
        total = this.selectedProperties.length
          + (this.isDateSelected ? 1 : 0)
          + this.selectedSeasons.length
          + (this.isFanExclusiveSelected ? 1 : 0)
          + (this.isEliteFanExclusiveSelected ? 1 : 0)
          + (this.isOnlineBookingSelected ? 1 : 0)
        return total
      },

      hasFilterSelected() {
        return this.selectedProperties.length > 0
          || this.isDateSelected
          || this.selectedSeasons.length > 0
          || this.isFanExclusiveSelected
          || this.isEliteFanExclusiveSelected
          || this.isOnlineBookingSelected
      },

      todaysDate() {
        return moment().format('YYYY-MM-DD')
      },

      oneYearLaterDate() {
        return moment().add(1, 'year').format('YYYY-MM-DD')
      },

      hasCategorySelected() {
        return !!Object.keys(this.experienceTypeFilter).length
      },

      isRegionCheckboxDisabled() {
        return this.availablePropertiesByRegion.every((region) => {
          const allRegionHotels = region.properties.map((property) => property.name)
          return allRegionHotels.every((propertyName) => this.propertyIsDisabled(propertyName))
        })
      },
      showFanExclusiveFilter() {
        return this.options.showExclusiveSetting
      },
      shouldShowPropertyFilter() {
        const uniqueProperties = [...new Set(this.experiences.map((item) => item.property?.name))]
        return uniqueProperties.length !== 1
      },
      exclusiveExperiencesExist() {
        return this.filteredExperiencesByCategory?.some((item) => item.isFansOfMOExclusive)
      },
      eliteExclusiveExperiencesExist() {
        return this.filteredExperiencesByCategory?.some((item) => item.isFansOfMOEliteExclusive)
      },
      onlineBookingSettingExist() {
        return this.filteredExperiencesByCategory?.some((item) => item.onlineBookingAvailable)
      },
    },
    watch: {
      experienceTypeFilter() {
        this.filterExperiences()
      },
      selectedProperties() {
        this.filterExperiences()
      },
      selectedSeasons() {
        this.filterExperiences()
      },
      isFanExclusiveSelected() {
        this.filterExperiences()
      },
      isEliteFanExclusiveSelected() {
        this.filterExperiences()
      },
      isOnlineBookingSelected() {
        this.filterExperiences()
      },
      selectedDates: {
        handler(val) {
          this.isDateSelected = !!(val.startDate && val.endDate)
          this.filterExperiences()
        },
        deep: true
      }
    },
    mounted() {
      this.filterExperiences()
    },
    methods: {
      handleStartDateChange(date) {
        this.selectedDates.startDate = date
        if (!this.selectedDates.endDate) {
          this.selectedDates.endDate = date
          this.$refs.endDate.setDate(date)
        }
      },

      handleEndDateChange(date) {
        this.selectedDates.endDate = date
        if (!this.selectedDates.startDate) {
          this.selectedDates.startDate = date
          this.$refs.startDate.setDate(date)
        }
      },

      resetDateFilter() {
        this.$refs.endDate.setDate(null)
        this.$refs.startDate.setDate(null)
        this.selectedDates.startDate = null
        this.selectedDates.endDate = null
      },

      resetPropertyFilter() {
        this.selectedProperties = []
        this.availablePropertiesByRegion.forEach((region) => {
          region.isRegionCheckboxDisabled = false
        })
      },

      resetAllFilters() {
        this.isFanExclusiveSelected = false
        this.isEliteFanExclusiveSelected = false
        this.isOnlineBookingSelected = false
        this.selectedProperties = []
        this.selectedDates.startDate = null
        this.selectedDates.endDate = null
        this.selectedSeasons = []

        this.availablePropertiesByRegion.forEach((region) => {
          region.isRegionCheckboxDisabled = false
        })
      },

      handleCloseModal() {
        document.body.classList.remove('no-scroll')
        this.isOpen = false
      },

      handleOpenModal() {
        document.body.classList.add('no-scroll')
        this.isOpen = true
        this.areFilterSectionsExpanded()
      },

      getAvailableProperties(items) {
        const properties = []
        const allProperties = items.filter((item) => item.property !== null).map((item) => item.property)

        if (allProperties && allProperties.length > 0) {
          allProperties.forEach((item) => {
            const isDuplicate = properties.some((property) => property.id === item.id)
            if (item && !isDuplicate) {
              properties.push(item)
            }
          })
        }

        return properties
      },

      getAvailableSeasons(items) {
        const uniqueSeasonsMap = {}

        items.forEach((item) => {
          item.seasons.forEach((season) => {
            uniqueSeasonsMap[season.id] = season
          })
        })
        return Object.values(uniqueSeasonsMap)
      },

      updateAvailableFilters() {
        const withoutPropertyFilter = this.getFilteredExperiences(this.filteredExperiencesByCategory, 'property')
        const withoutSeasonFilter = this.getFilteredExperiences(this.filteredExperiencesByCategory, 'season')
        this.availableProperties = this.getAvailableProperties(withoutPropertyFilter)
        this.availableSeasons = this.getAvailableSeasons(withoutSeasonFilter)
      },

      getFilteredExperiences(experinces, skipFilter = '') {
        return experinces.filter((item) => {
          if (this.isFanExclusiveSelected && !item.isFansOfMOExclusive) return false
          if (this.isEliteFanExclusiveSelected && !item.isFansOfMOEliteExclusive) return false
          if (this.isOnlineBookingSelected && !item.onlineBookingAvailable) return false
          if (this.selectedProperties.length > 0 && skipFilter !== 'property') {
            const itemProperty = item.property.name
            const isPropertyMatch = this.selectedProperties.some((selectedProperty) => itemProperty === selectedProperty)

            if (!isPropertyMatch) {
              return false
            }
          }

          if (this.isDateSelected) {
            const itemStartDate = moment(item.startDate, 'YYYY-MM-DD')
            const itemEndDate = moment(item.endDate, 'YYYY-MM-DD')
            const selectedStartDate = moment(this.selectedDates.startDate, 'YYYY-MM-DD')
            const selectedEndDate = moment(this.selectedDates.endDate, 'YYYY-MM-DD')
            const startsBeforeRange = itemStartDate.isSameOrBefore(selectedEndDate)
            const endsAfterRange = itemEndDate.isSameOrAfter(selectedStartDate)

            if (!startsBeforeRange || !endsAfterRange) {
              return false
            }
          }

          if (this.selectedSeasons.length > 0 && skipFilter !== 'season') {
            const itemSeasons = item.seasons.map((season) => season.name)
            const isSeasonMatch = this.selectedSeasons.some((selectedSeason) => itemSeasons.includes(selectedSeason))

            if (!isSeasonMatch) {
              return false
            }
          }

          return true
        })
      },

      filterExperiences() {
        this.filteredExperiences = this.getFilteredExperiences(this.filteredExperiencesByCategory)
        this.updateAvailableFilters()
        this.$emit('updateFilteredExperiences', this.filteredExperiences)
      },

      selectAllPropertiesInRegion(regionName) {
        const region = this.availablePropertiesByRegion.find((item) => item.name === regionName)

        if (region) {
          const nonDisabledProperties = region.properties.filter((property) => !this.propertyIsDisabled(property.name))

          if (nonDisabledProperties.length > 0) {
            const allSelected = region.properties.every((property) => this.selectedProperties.includes(property.name))
            if (allSelected) {
              region.properties.forEach((property) => {
                const index = this.selectedProperties.indexOf(property.name)
                if (index !== -1) {
                  this.selectedProperties.splice(index, 1)
                }
              })
            } else {
              const allNonDisabledSelected = nonDisabledProperties.every((property) => this.selectedProperties.includes(property.name))
              if (allNonDisabledSelected) {
                nonDisabledProperties.forEach((property) => {
                  const index = this.selectedProperties.indexOf(property.name)
                  if (index !== -1) {
                    this.selectedProperties.splice(index, 1)
                  }
                })
              } else {
                nonDisabledProperties.forEach((property) => {
                  if (!this.selectedProperties.includes(property.name)) {
                    this.selectedProperties.push(property.name)
                  }
                })
              }
            }
          }
          this.filterExperiences()
        }
      },

      areAllRegionPropertiesSelected(region) {
        const selectedProperties = region.properties.filter((property) => !this.propertyIsDisabled(property.name)).map((property) => property.name)
        return selectedProperties.length > 0 && selectedProperties.every((item) => this.selectedProperties.includes(item))
      },

      propertyIsDisabled(property) {
        if (!this.isDateSelected && this.selectedSeasons.length === 0 && !this.isFanExclusiveSelected && !this.isOnlineBookingSelected && !this.isEliteFanExclusiveSelected) return false
        return !this.availableProperties.some((item) => item.name === property)
      },

      seasonIsDisabled(season) {
        return !this.availableSeasons.some((item) => item.name === season)
      },

      fanExlusiveIsDisabled() {
        return !this.filteredExperiences.some((item) => item.isFansOfMOExclusive)
      },
      eliteFanExlusiveIsDisabled() {
        return !this.filteredExperiences.some((item) => item.isFansOfMOEliteExclusive)
      },
      onlineBookingIsDisabled() {
        return !this.filteredExperiences.some((item) => item.onlineBookingAvailable)
      },
      regionIsDisabled(region) {
        const allRegionProperties = region.properties.map((property) => property.name)
        return allRegionProperties.every((hotelName) => this.propertyIsDisabled(hotelName))
      },

      formatDateRange(startDate, endDate) {
        const formattedStartDate = moment(startDate).format('MMM, DD')
        const formattedEndDate = moment(endDate).format('MMM, DD')

        return `${formattedStartDate} – ${formattedEndDate}`
      },

      areFilterSectionsExpanded() {
        this.$nextTick(() => {
          if (this.$refs.accordionsWrapper) {
            const accordionsWrapperHeight = this.$refs.accordionsWrapper.clientHeight
            const filterSidebarBody = this.wHeight
              - this.$refs.filterSidebar.$refs.header.clientHeight
              - this.$refs.filterSidebar.$refs.footer.clientHeight
            const expandFilterSection = accordionsWrapperHeight <= filterSidebarBody

            if (this.$refs.accordionProperty) {
              this.$refs.accordionProperty.expanded = expandFilterSection
            }
            if (this.$refs.accordionDate) {
              this.$refs.accordionDate.expanded = expandFilterSection
            }
            if (this.$refs.accordionSeason) {
              this.$refs.accordionSeason.expanded = expandFilterSection
            }
          }
        })
      }
    },
  }
</script>
